import { ETLCourse } from './types';

export const INFORMATICA_COURSE: ETLCourse = {
  id: 'informatica-powercenter',
  title: 'Informatica PowerCenter Mastery',
  description: 'Master the industry-leading ETL platform. Learn to design, develop, and deploy enterprise data integration solutions using Informatica PowerCenter.',
  level: 'intermediate',
  totalDuration: '14-18 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Informatica',
  marketDemand: 'high',
  salaryRange: '$95,000 - $160,000',
  prerequisites: [
    'Strong SQL and database knowledge',
    'Data warehousing concepts',
    'Unix/Linux basics',
    'Understanding of ETL processes'
  ],
  learningOutcomes: [
    'Design complex Informatica mappings',
    'Build reusable transformations',
    'Implement data quality frameworks',
    'Deploy workflows to production',
    'Optimize performance and troubleshoot',
    'Manage enterprise ETL environments'
  ],
  modules: [
    {
      id: 'informatica-1',
      title: 'Informatica PowerCenter Architecture and Basics',
      description: 'Understand PowerCenter architecture, components, and development environment setup',
      duration: '3 hours',
      difficulty: 'beginner',
      order: 1,
      objectives: [
        'Understand Informatica PowerCenter architecture',
        'Navigate PowerCenter Client tools',
        'Create basic mappings and transformations',
        'Configure connections and folders'
      ],
      content: `# Informatica PowerCenter Architecture and Basics

## PowerCenter Architecture Overview

Informatica PowerCenter is an enterprise data integration platform that provides a complete data integration solution for all data integration requirements.

### Core Components

**1. PowerCenter Repository**
- Stores metadata for all objects
- Versioning and security management
- Shared development environment

**2. PowerCenter Server (Integration Service)**
- Executes workflows and sessions
- Data processing engine
- Load balancing and high availability

**3. PowerCenter Client Tools**
- Designer: Create mappings and transformations
- Workflow Manager: Create workflows and tasks
- Workflow Monitor: Monitor execution
- Repository Manager: Manage repository and users

**4. PowerCenter Repository Service**
- Manages repository database
- Handles user authentication
- Provides metadata services

## PowerCenter Client Tools

### Designer Tool

**Purpose**: Create and modify mappings, transformations, and mapplets

**Key Features**:
- Source Analyzer: Import source definitions
- Warehouse Designer: Create target definitions  
- Transformation Developer: Build reusable transformations
- Mapplet Designer: Create reusable mapping components
- Mapping Designer: Create data flow mappings

### Workflow Manager

**Purpose**: Create and manage workflows, sessions, and tasks

**Components**:
- Task Developer: Create reusable tasks
- Worklet Designer: Create reusable workflow components
- Workflow Designer: Orchestrate execution flow

### Setting Up Your First Project

**1. Create Repository Connection**
\`\`\`
Repository Name: REP_DEV
User Name: developer
Password: [password]
Repository Service: pc_repo_service
Domain: INFA_DOMAIN
\`\`\`

**2. Create Folder Structure**
\`\`\`
Project Folders:
├── SRC_DEFINITIONS (Source definitions)
├── TGT_DEFINITIONS (Target definitions)
├── TRANSFORMATIONS (Reusable transformations)
├── MAPPLETS (Reusable mapplets)
├── MAPPINGS (Data flow mappings)
├── SESSIONS (Session configurations)
├── WORKFLOWS (Workflow definitions)
└── CONNECTIONS (Connection objects)
\`\`\`

## Source and Target Definitions

### Importing Source Definitions

**From Relational Database:**
\`\`\`sql
-- Source table: CUSTOMERS
CREATE TABLE CUSTOMERS (
    CUSTOMER_ID NUMBER(10) PRIMARY KEY,
    FIRST_NAME VARCHAR2(50) NOT NULL,
    LAST_NAME VARCHAR2(50) NOT NULL,
    EMAIL VARCHAR2(100),
    PHONE VARCHAR2(20),
    ADDRESS VARCHAR2(200),
    CITY VARCHAR2(50),
    STATE VARCHAR2(50),
    ZIP_CODE VARCHAR2(10),
    COUNTRY VARCHAR2(50),
    CREATED_DATE DATE DEFAULT SYSDATE,
    MODIFIED_DATE DATE DEFAULT SYSDATE
);
\`\`\`

**Source Definition Properties:**
- Business Name: Customer Source
- Database Type: Oracle
- Owner: SOURCEDB
- Table Type: Table

**From Flat File:**
\`\`\`
File Format: Fixed Width
Record Type: Fixed Length
Record Length: 255

Columns:
CUSTOMER_ID (1-10, Number, Precision 10)
FIRST_NAME (11-60, String, Length 50)
LAST_NAME (61-110, String, Length 50)
EMAIL (111-210, String, Length 100)
PHONE (211-230, String, Length 20)
\`\`\`

### Creating Target Definitions

**Data Warehouse Target:**
\`\`\`sql
-- Target table: DIM_CUSTOMER
CREATE TABLE DIM_CUSTOMER (
    CUSTOMER_KEY NUMBER(10) GENERATED ALWAYS AS IDENTITY PRIMARY KEY,
    CUSTOMER_ID NUMBER(10) NOT NULL,
    CUSTOMER_NAME VARCHAR2(100),
    EMAIL VARCHAR2(100),
    PHONE VARCHAR2(20), 
    FULL_ADDRESS VARCHAR2(300),
    CUSTOMER_TYPE VARCHAR2(20),
    EFFECTIVE_DATE DATE NOT NULL,
    EXPIRATION_DATE DATE,
    IS_CURRENT CHAR(1) DEFAULT 'Y',
    CREATED_BY VARCHAR2(50) DEFAULT 'INFORMATICA',
    CREATED_DATE DATE DEFAULT SYSDATE,
    MODIFIED_BY VARCHAR2(50) DEFAULT 'INFORMATICA',
    MODIFIED_DATE DATE DEFAULT SYSDATE
);
\`\`\`

## Basic Transformations

### Source Qualifier Transformation

**Purpose**: Interface between source definition and other transformations

**Properties Configuration:**
\`\`\`
SQL Query: SELECT * FROM CUSTOMERS WHERE MODIFIED_DATE >= $$LAST_EXTRACT_DATE
User Defined Join: (for multiple sources)
Source Filter: COUNTRY = 'USA'
Number of Sorted Ports: 0
Tracing Level: Normal
Output is Deterministic: Yes
\`\`\`

### Expression Transformation

**Purpose**: Calculate values for a single row

**Common Expressions:**
\`\`\`
-- Concatenate names
CUSTOMER_NAME = FIRST_NAME || ' ' || LAST_NAME

-- Extract email domain
EMAIL_DOMAIN = SUBSTR(EMAIL, INSTR(EMAIL, '@') + 1)

-- Format phone number
FORMATTED_PHONE = 
CASE 
    WHEN LENGTH(PHONE) = 10 THEN 
        '(' || SUBSTR(PHONE, 1, 3) || ') ' || 
        SUBSTR(PHONE, 4, 3) || '-' || 
        SUBSTR(PHONE, 7, 4)
    ELSE PHONE
END

-- Create address
FULL_ADDRESS = ADDRESS || ', ' || CITY || ', ' || STATE || ' ' || ZIP_CODE

-- Current timestamp
CURRENT_TIMESTAMP = SYSDATE

-- Customer type based on business rules
CUSTOMER_TYPE = 
CASE
    WHEN EMAIL IS NOT NULL AND PHONE IS NOT NULL THEN 'Premium'
    WHEN EMAIL IS NOT NULL OR PHONE IS NOT NULL THEN 'Standard'
    ELSE 'Basic'
END
\`\`\`

### Filter Transformation

**Purpose**: Filter rows based on conditions

**Filter Conditions:**
\`\`\`
-- Valid email addresses
EMAIL IS NOT NULL AND INSTR(EMAIL, '@') > 0 AND INSTR(EMAIL, '.') > 0

-- US customers only
COUNTRY = 'USA'

-- Recent customers (last 5 years)
CREATED_DATE >= ADD_TO_DATE(SYSDATE, 'YY', -5)

-- Complete records
FIRST_NAME IS NOT NULL AND LAST_NAME IS NOT NULL AND EMAIL IS NOT NULL
\`\`\`

### Router Transformation

**Purpose**: Route data to multiple targets based on conditions

**Group Configuration:**
\`\`\`
Group 1 (PREMIUM_CUSTOMERS):
Condition: CUSTOMER_TYPE = 'Premium'

Group 2 (STANDARD_CUSTOMERS):
Condition: CUSTOMER_TYPE = 'Standard'

Group 3 (BASIC_CUSTOMERS):
Condition: CUSTOMER_TYPE = 'Basic'

Default Group:
All remaining records
\`\`\`

## Basic Mapping Development

### Simple Load Mapping

**Mapping: m_CUSTOMER_LOAD**

**Flow:**
\`\`\`
SRC: CUSTOMERS (Source)
↓
SQ_CUSTOMERS (Source Qualifier)
↓
EXP_CUSTOMER_TRANSFORM (Expression)
↓
FIL_VALID_CUSTOMERS (Filter)
↓
TGT: DIM_CUSTOMER (Target)
\`\`\`

**Transformation Logic:**
1. **Source Qualifier**: Extract modified records
2. **Expression**: Calculate derived fields
3. **Filter**: Remove invalid records
4. **Target**: Load to dimension table

### Mapping with Lookup

**Mapping: m_CUSTOMER_SCD1**

**Flow:**
\`\`\`
SRC: CUSTOMERS (Source)
↓
SQ_CUSTOMERS (Source Qualifier)
↓
LKP_EXISTING_CUSTOMER (Lookup)
↓
EXP_SCD_LOGIC (Expression)
↓
RTR_INSERT_UPDATE (Router)
├─ INSERT_GROUP → TGT: DIM_CUSTOMER (Insert)
└─ UPDATE_GROUP → UPD_CUSTOMER_UPDATE (Update Strategy) → TGT: DIM_CUSTOMER (Update)
\`\`\`

**Lookup Transformation Configuration:**
\`\`\`
Lookup Source: DIM_CUSTOMER
Lookup Condition: CUSTOMER_ID = IN_CUSTOMER_ID
Return Fields: CUSTOMER_KEY, CUSTOMER_NAME, EMAIL, PHONE, MODIFIED_DATE
Lookup Policy: Use First Value, Use Last Value
Lookup Cache: Persistent Cache
\`\`\`

## Session and Workflow Configuration

### Session Configuration

**Session: s_m_CUSTOMER_LOAD**

**Source Connection:**
\`\`\`
Connection Name: SOURCE_DB
Connection Type: Oracle
User Name: source_user
Password: [encrypted]
Connect String: source_server:1521:ORCL
\`\`\`

**Target Connection:**
\`\`\`
Connection Name: TARGET_DW
Connection Type: Oracle
User Name: dw_user
Password: [encrypted]
Connect String: dw_server:1521:DWDB
\`\`\`

**Session Properties:**
\`\`\`
Target Load Type: Normal
Commit Type: Target
Commit Interval: 10000
Error Handling: Continue on Error
Stop on Error: 0
Tracing Level: Normal
Save session log by: Session Runs
Success Email: dev-team@company.com
Failure Email: support-team@company.com
\`\`\`

### Workflow Configuration

**Workflow: wf_CUSTOMER_ETL**

**Workflow Structure:**
\`\`\`
START
↓
tsk_GET_EXTRACT_DATE (Assignment Task)
↓
s_m_CUSTOMER_LOAD (Session)
↓
tsk_UPDATE_EXTRACT_DATE (Assignment Task)
↓
tsk_EMAIL_SUCCESS (Email Task)
↓
END
\`\`\`

**Assignment Task Configuration:**
\`\`\`
Task: tsk_GET_EXTRACT_DATE
Type: Assignment
Variable Assignment:
$$LAST_EXTRACT_DATE = $$LAST_EXTRACT_DATE
Value: TO_CHAR(SYSDATE-1, 'YYYY-MM-DD')
\`\`\`

## Connection Objects

### Relational Connection

**Oracle Connection:**
\`\`\`
Connection Name: ORACLE_SOURCE
Connection Type: Oracle
User Name: etl_user
Password: [use parameter $$DB_PASSWORD]
Connect String: $PMSourceConnectionString
Codepage: UTF-8
Transaction Environment: Ignore
Connection Environment SQL: ALTER SESSION SET NLS_DATE_FORMAT='YYYY-MM-DD HH24:MI:SS'
\`\`\`

### File Connection

**Flat File Connection:**
\`\`\`
Connection Name: FILE_SOURCE
Connection Type: FTP
Host: ftp.company.com
Port: 21
User Name: ftp_user
Password: [encrypted]
Remote Directory: /incoming/customer_data
Codepage: UTF-8
\`\`\`

## Parameters and Variables

### Mapping Parameters

**Parameter File: CUSTOMER_ETL.par**
\`\`\`
[GLOBAL]
$$LAST_EXTRACT_DATE=2023-01-01

[FOLDER.WF:wf_CUSTOMER_ETL]
$$BATCH_SIZE=10000
$$ERROR_THRESHOLD=100

[FOLDER.WF:wf_CUSTOMER_ETL.ST:s_m_CUSTOMER_LOAD]
$$SOURCE_FILTER=COUNTRY='USA'
$$TARGET_OPTIONS=TRUNCATETABLE
\`\`\`

### Workflow Variables

**Variable Definition:**
\`\`\`
$$PROCESS_DATE = TO_CHAR(SYSDATE, 'YYYYMMDD')
$$RECORD_COUNT = 0
$$ERROR_COUNT = 0
$$FILE_NAME = 'CUSTOMER_' || $$PROCESS_DATE || '.csv'
\`\`\`

## Best Practices

### Naming Conventions

**Objects:**
\`\`\`
Sources: SRC_[TABLE_NAME]
Targets: TGT_[TABLE_NAME]
Mappings: m_[BUSINESS_PROCESS]
Sessions: s_[MAPPING_NAME]
Workflows: wf_[BUSINESS_PROCESS]
Transformations: [TYPE]_[PURPOSE]
  - EXP_CUSTOMER_CALC
  - LKP_PRODUCT_INFO
  - FIL_VALID_RECORDS
\`\`\`

### Performance Guidelines

1. **Source Qualifier Optimization**
   - Use SQL override for complex logic
   - Filter data at source level
   - Sort data if possible

2. **Transformation Best Practices**
   - Minimize transformations in data flow
   - Use persistent lookup cache
   - Implement error handling

3. **Session Configuration**
   - Appropriate commit intervals
   - Optimal DTM buffer settings
   - Connection pooling

### Documentation Standards

**Mapping Documentation:**
\`\`\`
Business Purpose: Load customer dimension with SCD Type 1 logic
Data Sources: OLTP Customer table
Target: Data warehouse customer dimension
Business Rules:
- Overwrite changed attributes
- Maintain audit fields
- Filter for valid email addresses
Performance Notes:
- Lookup cache size: 1GB
- Expected volume: 100K records daily
\`\`\`

## Next Steps

In the next module, we'll cover:
- Advanced transformations (Aggregator, Joiner, Union)
- Slowly Changing Dimensions (SCD Types 1, 2, 3)
- Performance tuning techniques
- Error handling and recovery strategies
- Reusable components (Mapplets, Worklets)`
    },
    {
      id: 'informatica-2',
      title: 'Advanced Transformations and SCD Implementation',
      description: 'Master complex transformations, implement various SCD types, and build reusable components',
      duration: '4 hours',
      difficulty: 'intermediate',
      order: 2,
      objectives: [
        'Implement slowly changing dimensions (SCD Types 1, 2, 3)',
        'Master advanced transformations',
        'Build reusable mapplets and worklets',
        'Optimize transformation performance'
      ],
      content: `# Advanced Transformations and SCD Implementation

## Slowly Changing Dimensions

### SCD Type 1 - Overwrite

**Business Scenario**: Customer address changes should overwrite the existing record

**Mapping: m_CUSTOMER_SCD1**

**Mapping Flow:**
\`\`\`
SRC_CUSTOMERS
↓
SQ_CUSTOMERS
↓
LKP_EXISTING_CUSTOMER (Lookup on CUSTOMER_ID)
↓
EXP_SCD1_LOGIC
↓
RTR_INSERT_UPDATE
├─ NEW_RECORDS → TGT_DIM_CUSTOMER (Insert)
└─ CHANGED_RECORDS → UPD_SCD1_UPDATE → TGT_DIM_CUSTOMER (Update)
\`\`\`

**Lookup Transformation (LKP_EXISTING_CUSTOMER):**
\`\`\`sql
-- Lookup Table: DIM_CUSTOMER
-- Lookup Source Qualifier Query:
SELECT 
    CUSTOMER_KEY,
    CUSTOMER_ID,
    CUSTOMER_NAME,
    EMAIL,
    PHONE,
    ADDRESS,
    CITY,
    STATE,
    ZIP_CODE,
    MODIFIED_DATE
FROM DIM_CUSTOMER
WHERE IS_CURRENT = 'Y'

-- Lookup Condition:
CUSTOMER_ID = IN_CUSTOMER_ID
\`\`\`

**Expression Transformation (EXP_SCD1_LOGIC):**
\`\`\`
-- Flag for new vs existing records
IS_NEW_RECORD = IIF(ISNULL(LKP_CUSTOMER_KEY), 'Y', 'N')

-- Flag for changed records
IS_CHANGED = IIF(
    IS_NEW_RECORD = 'N' AND (
        NVL(CUSTOMER_NAME, 'NULL') != NVL(LKP_CUSTOMER_NAME, 'NULL') OR
        NVL(EMAIL, 'NULL') != NVL(LKP_EMAIL, 'NULL') OR
        NVL(PHONE, 'NULL') != NVL(LKP_PHONE, 'NULL') OR
        NVL(ADDRESS, 'NULL') != NVL(LKP_ADDRESS, 'NULL') OR
        NVL(CITY, 'NULL') != NVL(LKP_CITY, 'NULL') OR
        NVL(STATE, 'NULL') != NVL(LKP_STATE, 'NULL') OR
        NVL(ZIP_CODE, 'NULL') != NVL(LKP_ZIP_CODE, 'NULL')
    ), 'Y', 'N'
)

-- Set customer key for updates
OUT_CUSTOMER_KEY = IIF(IS_NEW_RECORD = 'Y', NULL, LKP_CUSTOMER_KEY)

-- Audit fields
CREATED_BY = IIF(IS_NEW_RECORD = 'Y', 'ETL_PROCESS', LKP_CREATED_BY)
CREATED_DATE = IIF(IS_NEW_RECORD = 'Y', SYSDATE, LKP_CREATED_DATE)
MODIFIED_BY = 'ETL_PROCESS'
MODIFIED_DATE = SYSDATE
\`\`\`

**Router Transformation (RTR_INSERT_UPDATE):**
\`\`\`
Group 1 - NEW_RECORDS:
Condition: IS_NEW_RECORD = 'Y'

Group 2 - CHANGED_RECORDS:
Condition: IS_CHANGED = 'Y'

Default Group: Unchanged records (no action)
\`\`\`

### SCD Type 2 - Historical Tracking

**Business Scenario**: Track historical changes in customer information

**Enhanced Target Table:**
\`\`\`sql
CREATE TABLE DIM_CUSTOMER_SCD2 (
    CUSTOMER_KEY NUMBER(10) GENERATED ALWAYS AS IDENTITY PRIMARY KEY,
    CUSTOMER_ID NUMBER(10) NOT NULL,
    CUSTOMER_NAME VARCHAR2(100),
    EMAIL VARCHAR2(100),
    PHONE VARCHAR2(20),
    ADDRESS VARCHAR2(200),
    CITY VARCHAR2(50),
    STATE VARCHAR2(50),
    ZIP_CODE VARCHAR2(10),
    EFFECTIVE_DATE DATE NOT NULL,
    EXPIRATION_DATE DATE,
    IS_CURRENT CHAR(1) DEFAULT 'Y',
    VERSION_NUMBER NUMBER(3) DEFAULT 1,
    CREATED_BY VARCHAR2(50) DEFAULT 'ETL_PROCESS',
    CREATED_DATE DATE DEFAULT SYSDATE,
    MODIFIED_BY VARCHAR2(50) DEFAULT 'ETL_PROCESS',
    MODIFIED_DATE DATE DEFAULT SYSDATE,
    
    CONSTRAINT CHK_IS_CURRENT CHECK (IS_CURRENT IN ('Y', 'N'))
);

-- Index for performance
CREATE INDEX IDX_DIM_CUSTOMER_SCD2_LOOKUP 
ON DIM_CUSTOMER_SCD2 (CUSTOMER_ID, IS_CURRENT);
\`\`\`

**Mapping: m_CUSTOMER_SCD2**

**Complex SCD2 Flow:**
\`\`\`
SRC_CUSTOMERS
↓
SQ_CUSTOMERS
↓
LKP_CURRENT_CUSTOMER (Lookup current record)
↓
EXP_SCD2_LOGIC (SCD2 calculations)
↓
RTR_SCD2_ROUTING
├─ NEW_RECORDS → EXP_NEW_RECORD_PREP → TGT_DIM_CUSTOMER_SCD2 (Insert)
├─ CHANGED_RECORDS → 
│   ├─ EXP_EXPIRE_OLD → UPD_EXPIRE_CURRENT → TGT_DIM_CUSTOMER_SCD2 (Update)
│   └─ EXP_INSERT_NEW → TGT_DIM_CUSTOMER_SCD2 (Insert)
└─ UNCHANGED_RECORDS → (No action)
\`\`\`

**Lookup Transformation (LKP_CURRENT_CUSTOMER):**
\`\`\`sql
SELECT 
    CUSTOMER_KEY,
    CUSTOMER_ID,
    CUSTOMER_NAME,
    EMAIL,
    PHONE,
    ADDRESS,
    CITY,
    STATE,
    ZIP_CODE,
    EFFECTIVE_DATE,
    VERSION_NUMBER
FROM DIM_CUSTOMER_SCD2
WHERE IS_CURRENT = 'Y'

-- Lookup Condition: CUSTOMER_ID = IN_CUSTOMER_ID
-- Return Fields: All listed above
-- Lookup Policy: Use First Value
-- Lookup Cache: Persistent Cache
\`\`\`

**Expression Transformation (EXP_SCD2_LOGIC):**
\`\`\`
-- Determine record status
IS_NEW = IIF(ISNULL(LKP_CUSTOMER_KEY), 'Y', 'N')

-- Check for changes (excluding audit fields)
HAS_CHANGE = IIF(
    IS_NEW = 'N' AND (
        NVL(CUSTOMER_NAME, 'NULL') != NVL(LKP_CUSTOMER_NAME, 'NULL') OR
        NVL(EMAIL, 'NULL') != NVL(LKP_EMAIL, 'NULL') OR
        NVL(PHONE, 'NULL') != NVL(LKP_PHONE, 'NULL') OR
        NVL(ADDRESS, 'NULL') != NVL(LKP_ADDRESS, 'NULL') OR
        NVL(CITY, 'NULL') != NVL(LKP_CITY, 'NULL') OR
        NVL(STATE, 'NULL') != NVL(LKP_STATE, 'NULL') OR
        NVL(ZIP_CODE, 'NULL') != NVL(LKP_ZIP_CODE, 'NULL')
    ), 'Y', 'N'
)

-- Set dates for SCD2
EFFECTIVE_DATE = SYSDATE
EXPIRATION_DATE = DATE('31-DEC-9999', 'DD-MON-YYYY')

-- Version management
NEW_VERSION = IIF(IS_NEW = 'Y', 1, LKP_VERSION_NUMBER + 1)

-- Prepare for expiring old record
OLD_CUSTOMER_KEY = LKP_CUSTOMER_KEY
OLD_EXPIRATION_DATE = SYSDATE - 1/86400  -- 1 second before new effective date
\`\`\`

**Router Groups:**
\`\`\`
NEW_RECORDS: IS_NEW = 'Y'
CHANGED_RECORDS: HAS_CHANGE = 'Y'
UNCHANGED_RECORDS: IS_NEW = 'N' AND HAS_CHANGE = 'N'
\`\`\`

**Update Strategy for Expiring Records:**
\`\`\`
Update Strategy Expression: DD_UPDATE
Key: CUSTOMER_KEY (from lookup)
Update Fields: EXPIRATION_DATE, IS_CURRENT, MODIFIED_BY, MODIFIED_DATE
\`\`\`

### SCD Type 3 - Hybrid Approach

**Target Table with Previous Values:**
\`\`\`sql
CREATE TABLE DIM_CUSTOMER_SCD3 (
    CUSTOMER_KEY NUMBER(10) GENERATED ALWAYS AS IDENTITY PRIMARY KEY,
    CUSTOMER_ID NUMBER(10) NOT NULL,
    CUSTOMER_NAME VARCHAR2(100),
    EMAIL VARCHAR2(100),
    PHONE VARCHAR2(20),
    
    -- Current address
    CURRENT_ADDRESS VARCHAR2(200),
    CURRENT_CITY VARCHAR2(50),
    CURRENT_STATE VARCHAR2(50),
    CURRENT_ZIP_CODE VARCHAR2(10),
    
    -- Previous address (SCD3 columns)
    PREVIOUS_ADDRESS VARCHAR2(200),
    PREVIOUS_CITY VARCHAR2(50),
    PREVIOUS_STATE VARCHAR2(50),
    PREVIOUS_ZIP_CODE VARCHAR2(10),
    
    ADDRESS_CHANGE_DATE DATE,
    
    CREATED_BY VARCHAR2(50) DEFAULT 'ETL_PROCESS',
    CREATED_DATE DATE DEFAULT SYSDATE,
    MODIFIED_BY VARCHAR2(50) DEFAULT 'ETL_PROCESS',
    MODIFIED_DATE DATE DEFAULT SYSDATE
);
\`\`\`

## Advanced Transformations

### Aggregator Transformation

**Business Scenario**: Calculate customer order summaries

**Mapping: m_CUSTOMER_ORDER_SUMMARY**

**Aggregator Configuration:**
\`\`\`
Group By Ports:
- CUSTOMER_ID
- CUSTOMER_NAME
- ORDER_YEAR

Aggregate Expressions:
TOTAL_ORDERS = COUNT(ORDER_ID)
TOTAL_AMOUNT = SUM(ORDER_AMOUNT)
AVG_ORDER_VALUE = AVG(ORDER_AMOUNT)
MIN_ORDER_DATE = MIN(ORDER_DATE)
MAX_ORDER_DATE = MAX(ORDER_DATE)
FIRST_ORDER_AMOUNT = FIRST(ORDER_AMOUNT, ORDER_DATE)
LAST_ORDER_AMOUNT = LAST(ORDER_AMOUNT, ORDER_DATE)

-- Conditional aggregation
HIGH_VALUE_ORDERS = SUM(IIF(ORDER_AMOUNT > 1000, ORDER_AMOUNT, 0))
HIGH_VALUE_COUNT = COUNT(IIF(ORDER_AMOUNT > 1000, ORDER_ID, NULL))

-- Nested aggregate
TOTAL_CUSTOMERS = COUNT(DISTINCT CUSTOMER_ID)
\`\`\`

**Advanced Aggregator Features:**
\`\`\`
-- Sorted Input: Yes (for better performance)
-- Incremental Aggregation: Yes (for incremental loads)
-- Aggregate Cache: Persistent Cache

-- Pre/Post Session Commands:
Pre-session: TRUNCATE TABLE AGG_TEMP_TABLE
Post-session: ANALYZE TABLE CUSTOMER_SUMMARY COMPUTE STATISTICS
\`\`\`

### Joiner Transformation

**Business Scenario**: Join customer and order data

**Master-Detail Join:**
\`\`\`
Master Source: CUSTOMERS (smaller dataset)
Detail Source: ORDERS (larger dataset)

Join Type: Normal Join
Join Condition: CUSTOMERS.CUSTOMER_ID = ORDERS.CUSTOMER_ID

Joiner Properties:
- Sorted Input: Yes
- Join Type: Normal Join (Inner Join)
- Cache Directory: /tmp/informatica_cache
- Joiner Data Cache Size: 64MB
- Joiner Index Cache Size: 16MB
\`\`\`

**Outer Join Example:**
\`\`\`
Master Source: CUSTOMERS
Detail Source: ORDERS

Join Type: Master Outer Join
Join Condition: CUSTOMERS.CUSTOMER_ID = ORDERS.CUSTOMER_ID

-- This returns all customers, even those without orders
-- Order fields will be NULL for customers without orders
\`\`\`

**Complex Join with Multiple Conditions:**
\`\`\`
Join Condition: 
CUSTOMERS.CUSTOMER_ID = ORDERS.CUSTOMER_ID AND
ORDERS.ORDER_DATE >= CUSTOMERS.REGISTRATION_DATE
\`\`\`

### Union Transformation

**Business Scenario**: Combine data from multiple similar sources

**Union Configuration:**
\`\`\`
Input Groups:
1. ONLINE_CUSTOMERS (Input Group 1)
2. RETAIL_CUSTOMERS (Input Group 2)
3. MOBILE_CUSTOMERS (Input Group 3)

Output Ports:
- CUSTOMER_ID (Union from all inputs)
- CUSTOMER_NAME (Union from all inputs)
- EMAIL (Union from all inputs)
- CHANNEL (Derived to identify source)

Union Strategy: Union All (includes duplicates)
\`\`\`

**Expression in Union:**
\`\`\`
CHANNEL = IIF(CUSTOMER_ID1 IS NOT NULL, 'ONLINE',
              IIF(CUSTOMER_ID2 IS NOT NULL, 'RETAIL', 'MOBILE'))

UNIFIED_CUSTOMER_ID = NVL(CUSTOMER_ID1, NVL(CUSTOMER_ID2, CUSTOMER_ID3))
\`\`\`

## Reusable Components

### Mapplets

**Business Purpose**: Create reusable address standardization logic

**Mapplet: mpl_ADDRESS_STANDARDIZATION**

**Mapplet Structure:**
\`\`\`
Input Transformation: INPUT
↓
EXP_ADDRESS_CLEANSE (Address cleaning logic)
↓
EXP_ADDRESS_VALIDATE (Validation rules)
↓
Output Transformation: OUTPUT
\`\`\`

**Address Cleansing Logic:**
\`\`\`
-- Standardize street abbreviations
CLEAN_ADDRESS = REPLACE(REPLACE(REPLACE(
    UPPER(TRIM(IN_ADDRESS)),
    ' ST ', ' STREET '),
    ' AVE ', ' AVENUE '),
    ' RD ', ' ROAD '
)

-- Standardize apartment/unit
CLEAN_ADDRESS = REPLACE(REPLACE(
    CLEAN_ADDRESS,
    ' APT ', ' APARTMENT '),
    ' STE ', ' SUITE '
)

-- Clean phone numbers
CLEAN_PHONE = REGEXP_REPLACE(IN_PHONE, '[^0-9]', '')

-- Format phone
FORMATTED_PHONE = IIF(
    LENGTH(CLEAN_PHONE) = 10,
    '(' || SUBSTR(CLEAN_PHONE, 1, 3) || ') ' ||
    SUBSTR(CLEAN_PHONE, 4, 3) || '-' ||
    SUBSTR(CLEAN_PHONE, 7, 4),
    CLEAN_PHONE
)

-- Validation flags
IS_VALID_ADDRESS = IIF(LENGTH(CLEAN_ADDRESS) >= 10, 'Y', 'N')
IS_VALID_PHONE = IIF(LENGTH(CLEAN_PHONE) = 10, 'Y', 'N')
IS_VALID_ZIP = IIF(REGEXP_INSTR(IN_ZIP, '^[0-9]{5}(-[0-9]{4})?$') > 0, 'Y', 'N')
\`\`\`

**Using Mapplet in Mapping:**
\`\`\`
SRC_CUSTOMERS
↓
SQ_CUSTOMERS
↓
mpl_ADDRESS_STANDARDIZATION (Mapplet instance)
↓
FIL_VALID_ADDRESSES
↓
TGT_DIM_CUSTOMER
\`\`\`

### Worklets

**Business Purpose**: Reusable workflow components for common tasks

**Worklet: wkl_FILE_PROCESSING**

**Worklet Structure:**
\`\`\`
START
↓
tsk_CHECK_FILE_EXISTS (Command Task)
↓
tsk_BACKUP_FILE (Command Task)
↓
tsk_VALIDATE_FILE_FORMAT (Command Task)
↓
END
\`\`\`

**Command Task Examples:**
\`\`\`bash
# Check file exists
if [ -f "$$INPUT_FILE_PATH" ]; then
    echo "File found: $$INPUT_FILE_PATH"
    exit 0
else
    echo "File not found: $$INPUT_FILE_PATH"
    exit 1
fi

# Backup file
cp "$$INPUT_FILE_PATH" "$$BACKUP_DIRECTORY/$(basename $$INPUT_FILE_PATH).$(date +%Y%m%d_%H%M%S)"
echo "File backed up successfully"

# Validate file format
line_count=$(wc -l < "$$INPUT_FILE_PATH")
if [ $line_count -gt 0 ]; then
    echo "File contains $line_count lines"
    exit 0
else
    echo "File is empty"
    exit 1
fi
\`\`\`

## Performance Optimization

### Lookup Transformation Optimization

**Persistent Cache Configuration:**
\`\`\`
Cache Type: Persistent Cache
Cache File Name Prefix: LKP_CUSTOMER_
Cache Directory: /cache/informatica/persistent
Recache from Source: No
Cache File Time Limit: 7 (days)

-- Optimize lookup query
SELECT /*+ INDEX(DIM_CUSTOMER IDX_CUSTOMER_LOOKUP) */
    CUSTOMER_KEY,
    CUSTOMER_ID,
    CUSTOMER_NAME,
    EMAIL,
    MODIFIED_DATE
FROM DIM_CUSTOMER
WHERE IS_CURRENT = 'Y'
\`\`\`

**Shared Cache:**
\`\`\`
-- Multiple lookups can share the same cache
-- Configure in session properties:
$PMCacheDir/SHARED/LKP_CUSTOMER_SHARED
\`\`\`

### Session-Level Optimization

**DTM Configuration:**
\`\`\`
Buffer Block Size: 128000
Commit Interval: 10000
Collect Performance Data: Yes
Optimization Level: Medium

-- Advanced properties
Default Buffer Block Size: 64000
Index Cache Size: 16000000
Data Cache Size: 64000000
\`\`\`

**Parallel Processing:**
\`\`\`
-- Partition the source data
Source Partitioning: Key Range Partitioning
Partition Points: 4
Partition Key: CUSTOMER_ID

-- Configure parallel targets
Target Load Type: Bulk
Target Connection: Multiple connections for parallel loads
\`\`\`

## Error Handling Strategies

### Mapping-Level Error Handling

**Error Port Configuration:**
\`\`\`
-- In target transformation
Error Port: Enabled
Error File: /error_logs/customer_errors_$$SESS_START_TS.err
Error Rows: Continue

-- Error threshold in session
Stop on Error: 1000
\`\`\`

**Custom Error Logging:**
\`\`\`
-- Error logging target
CREATE TABLE ETL_ERROR_LOG (
    ERROR_ID NUMBER GENERATED ALWAYS AS IDENTITY PRIMARY KEY,
    MAPPING_NAME VARCHAR2(100),
    SOURCE_ROW CLOB,
    ERROR_MESSAGE VARCHAR2(4000),
    ERROR_TIMESTAMP DATE DEFAULT SYSDATE,
    SESSION_ID VARCHAR2(100)
);

-- Route error records to error table
RTR_ERROR_HANDLING
├─ VALID_RECORDS → Main processing flow
└─ ERROR_RECORDS → TGT_ETL_ERROR_LOG
\`\`\`

## Next Steps

In the next module, we'll cover:
- Advanced workflow orchestration
- Production deployment strategies
- Monitoring and performance tuning
- Integration with other Informatica tools
- Best practices for enterprise environments`
    },
    {
      id: 'informatica-3',
      title: 'Production Deployment and Enterprise Management',
      description: 'Deploy Informatica solutions to production, implement monitoring, and manage enterprise environments',
      duration: '4 hours',
      difficulty: 'advanced',
      order: 3,
      objectives: [
        'Deploy solutions across environments',
        'Implement comprehensive monitoring',
        'Manage enterprise ETL operations',
        'Troubleshoot performance issues'
      ],
      content: `# Production Deployment and Enterprise Management

## Environment Management Strategy

### Multi-Environment Architecture

**Environment Setup:**
\`\`\`
Development Environment:
- Repository: REP_DEV
- Integration Service: IS_DEV
- Purpose: Development and unit testing
- Data Volume: Sample datasets (10-20% of production)

Test Environment:
- Repository: REP_TEST
- Integration Service: IS_TEST
- Purpose: System testing and validation
- Data Volume: Full production volume

Production Environment:
- Repository: REP_PROD
- Integration Service: IS_PROD
- Purpose: Live data processing
- Data Volume: Full enterprise data
- High availability: Active-passive cluster

Disaster Recovery:
- Repository: REP_DR
- Integration Service: IS_DR
- Purpose: Business continuity
- Data sync: Real-time replication
\`\`\`

### Deployment Process

**Deployment Checklist:**
\`\`\`
Pre-Deployment:
□ Code review completed
□ Unit testing passed
□ Integration testing completed
□ Performance testing validated
□ Security review approved
□ Change management approval
□ Backup current production version
□ Environment readiness verified

Deployment Steps:
□ Export objects from source repository
□ Import objects to target repository
□ Validate object dependencies
□ Update connection objects
□ Configure session properties
□ Update parameter files
□ Test workflow execution
□ Validate data quality
□ Update monitoring configurations
□ Document deployment

Post-Deployment:
□ Smoke testing completed
□ Performance monitoring enabled
□ User acceptance testing
□ Production readiness sign-off
□ Documentation updated
□ Knowledge transfer completed
\`\`\`

## Repository Management

### Repository Administration

**Repository Backup Strategy:**
\`\`\`sql
-- Daily backup script
EXPDP informatica/password@INFA_REP 
DIRECTORY=BACKUP_DIR 
DUMPFILE=REP_BACKUP_$(date +%Y%m%d).dmp 
LOGFILE=REP_BACKUP_$(date +%Y%m%d).log
COMPRESSION=ALL
FULL=Y

-- Retention policy
find /backup/repository -name "*.dmp" -mtime +30 -delete
\`\`\`

**Version Control Integration:**
\`\`\`bash
#!/bin/bash
# Export repository objects for version control

# Set environment variables
INFA_HOME=/opt/informatica/powercenter
DOMAIN_NAME=INFA_DOMAIN
REPO_SERVICE=pc_repo_service
REPO_NAME=REP_PROD

# Export mappings
pmrep connect -r $REPO_NAME -d $DOMAIN_NAME -n admin -x admin_password

# Export by folder
pmrep objectexport -n MAPPINGS -o mapping -f SRC_FOLDER -u /exports/mappings/
pmrep objectexport -n WORKFLOWS -o workflow -f SRC_FOLDER -u /exports/workflows/
pmrep objectexport -n SESSIONS -o session -f SRC_FOLDER -u /exports/sessions/

# Commit to version control
git add /exports/*
git commit -m "Repository export - $(date)"
git push origin main
\`\`\`

### Security Management

**User and Group Management:**
\`\`\`
-- Repository users
CREATE_USER ETL_DEVELOPER WITH PASSWORD 'dev_password'
GRANT_PRIVILEGE READ,WRITE ON FOLDER ETL_DEVELOPMENT TO ETL_DEVELOPER

CREATE_USER ETL_OPERATOR WITH PASSWORD 'op_password'  
GRANT_PRIVILEGE EXECUTE ON FOLDER ETL_PRODUCTION TO ETL_OPERATOR

-- Security domains
DOMAIN_NATIVE: Native authentication
DOMAIN_LDAP: LDAP integration for enterprise authentication

-- Object-level security
FOLDER_PERMISSIONS:
- READ: View objects
- WRITE: Modify objects  
- EXECUTE: Run workflows
- ADMIN: Full administrative access
\`\`\`

## Advanced Workflow Orchestration

### Complex Workflow Patterns

**Master-Detail Processing Workflow:**
\`\`\`
wf_MASTER_DETAIL_PROCESSING:

START
↓
tsk_INITIALIZE_BATCH (Assignment Task)
├─ Variable: $$BATCH_ID = TO_CHAR(SYSDATE, 'YYYYMMDDHH24MISS')
└─ Variable: $$PROCESS_DATE = TO_CHAR(SYSDATE, 'YYYY-MM-DD')
↓
dec_CHECK_DATA_AVAILABILITY (Decision Task)
├─ Success: COUNT(*) > 0 FROM source tables
└─ Failure → tsk_SEND_NO_DATA_EMAIL
↓
wkl_PRE_PROCESSING (Worklet)
├─ Data validation
├─ Backup procedures
└─ Environment setup
↓
PARALLEL PROCESSING:
├─ s_m_LOAD_CUSTOMERS (Session)
├─ s_m_LOAD_PRODUCTS (Session)  
└─ s_m_LOAD_SUPPLIERS (Session)
↓
wkl_FACT_PROCESSING (Worklet)
├─ s_m_LOAD_ORDERS (Session)
└─ s_m_LOAD_ORDER_ITEMS (Session)
↓
wkl_POST_PROCESSING (Worklet)
├─ Data quality checks
├─ Reconciliation reports
└─ Archive procedures
↓
tsk_SUCCESS_EMAIL (Email Task)
↓
END

-- Error handling paths
ON_FAILURE → tsk_ERROR_EMAIL → tsk_CLEANUP → STOP
\`\`\`

**Event-Driven Workflow:**
\`\`\`
-- File watcher implementation
wf_FILE_WATCHER:

START
↓
TIMER (Timer Task - every 5 minutes)
↓ 
CMD_CHECK_FILES (Command Task)
-- Script: ls /incoming/*.csv | wc -l
↓
dec_FILES_AVAILABLE (Decision Task)
-- Condition: $$FILES_COUNT > 0
├─ True → wf_PROCESS_FILES (Start Workflow)
└─ False → TIMER (Loop back)

-- Dynamic file processing
wf_PROCESS_FILES:
FOR_EACH_FILE (Session with parameter $$FILE_NAME)
↓
s_m_PROCESS_SINGLE_FILE (Session)
↓
CMD_MOVE_PROCESSED_FILE (Command Task)
\`\`\`

### Workflow Dependencies

**Inter-Workflow Dependencies:**
\`\`\`
-- Parent workflow
wf_DAILY_ETL_MASTER:

START
↓
wf_DIMENSION_LOADS (Start Workflow)
├─ Run Mode: Wait for completion
└─ On Success only
↓
wf_FACT_LOADS (Start Workflow)
├─ Run Mode: Wait for completion  
└─ On Success only
↓
wf_AGGREGATE_LOADS (Start Workflow)
├─ Run Mode: Wait for completion
└─ On Success only
↓
wf_REPORTING_PREP (Start Workflow)
↓
END

-- Cross-system dependencies
EVENT_WAIT (Event Wait Task)
-- Event: UPSTREAM_SYSTEM_COMPLETE
-- Timeout: 4 hours
-- On timeout: Send alert and continue
\`\`\`

## Monitoring and Alerting

### Comprehensive Monitoring Framework

**Session Monitoring:**
\`\`\`sql
-- Monitoring queries
-- Current session status
SELECT 
    w.workflow_name,
    s.session_name,
    s.start_time,
    s.end_time,
    s.run_status_code,
    s.run_err_code,
    s.run_err_msg,
    s.src_success_rows,
    s.tgt_success_rows,
    s.tgt_failed_rows,
    ROUND((s.tgt_success_rows / NULLIF(s.src_success_rows, 0)) * 100, 2) as success_rate
FROM rep_sess_log s
JOIN rep_wflow_run w ON s.workflow_run_id = w.workflow_run_id
WHERE s.start_time >= SYSDATE - 1
ORDER BY s.start_time DESC;

-- Performance trends
SELECT 
    session_name,
    AVG(EXTRACT(HOUR FROM (end_time - start_time)) * 3600 + 
        EXTRACT(MINUTE FROM (end_time - start_time)) * 60 + 
        EXTRACT(SECOND FROM (end_time - start_time))) as avg_duration_seconds,
    AVG(src_success_rows) as avg_rows_processed,
    COUNT(*) as run_count
FROM rep_sess_log
WHERE start_time >= SYSDATE - 30
AND run_status_code = 1  -- Successful runs
GROUP BY session_name
ORDER BY avg_duration_seconds DESC;
\`\`\`

**Real-time Monitoring Dashboard:**
\`\`\`bash
#!/bin/bash
# Monitoring script for PowerCenter workflows

DOMAIN=INFA_DOMAIN
SERVICE=IS_PROD
REPO_SERVICE=pc_repo_service

# Check Integration Service status
pmcmd getservicedetails -d $DOMAIN -s $SERVICE

# Monitor currently running workflows
pmcmd getworkflowdetails -d $DOMAIN -s $SERVICE -folder PRODUCTION

# Check for failed sessions in last 24 hours
pmcmd getsessionlog -d $DOMAIN -s $SERVICE -folder PRODUCTION \
    -workflow wf_DAILY_ETL -session s_m_CUSTOMER_LOAD

# Performance metrics
pmcmd getworkflowlog -d $DOMAIN -s $SERVICE -folder PRODUCTION \
    -workflow wf_DAILY_ETL | grep -E "rows|time|error"
\`\`\`

### Alerting System

**Email Alert Configuration:**
\`\`\`
Email Task Configuration:
To: etl-team@company.com, support@company.com
CC: manager@company.com
Subject: [CRITICAL] ETL Failure - Workflow: $$WORKFLOW_NAME
Priority: High

Body Template:
================================
ETL FAILURE NOTIFICATION
================================

Workflow: $$WORKFLOW_NAME
Session: $$SESSION_NAME
Start Time: $$SESS_START_TS
Failure Time: $$ERROR_TIMESTAMP
Error Code: $$ERROR_CODE
Error Message: $$ERROR_MSG

Server Details:
Domain: $$DOMAIN_NAME
Integration Service: $$INTEGRATION_SERVICE_NAME
Repository: $$REPOSITORY_NAME

Performance Metrics:
Source Rows: $$SRC_SUCCESS_ROWS
Target Rows: $$TGT_SUCCESS_ROWS
Failed Rows: $$TGT_FAILED_ROWS

Please investigate immediately.

Automated ETL Monitoring System
================================
\`\`\`

**SNMP Integration for Enterprise Monitoring:**
\`\`\`bash
# SNMP trap configuration
# Send alerts to enterprise monitoring tools

snmptrap -v2c -c public monitoring-server.company.com '' \
    1.3.6.1.4.1.9999.1.1 \
    1.3.6.1.4.1.9999.1.1.1 s "ETL_FAILURE" \
    1.3.6.1.4.1.9999.1.1.2 s "$WORKFLOW_NAME" \
    1.3.6.1.4.1.9999.1.1.3 s "$ERROR_MSG" \
    1.3.6.1.4.1.9999.1.1.4 s "$(date)"
\`\`\`

## Performance Tuning

### Session-Level Optimization

**Memory Management:**
\`\`\`
Session Configuration:
- DTM Buffer Size: 12000000 (12MB per buffer)
- Buffer Block Size: 128000 (128KB per block)
- Index Cache Size: 16000000 (16MB)
- Data Cache Size: 64000000 (64MB)
- Buffer Memory Management: Auto

Advanced Properties:
- Collect Performance Data: Yes
- Write Backward Compatible Session Log: No
- Session Recovery Strategy: Resume from last checkpoint
- Checkpoint Directory: /checkpoint/informatica
\`\`\`

**Partitioning Strategy:**
\`\`\`
Source Partitioning:
Type: Key Range Partitioning
Partition Key: CUSTOMER_ID
Number of Partitions: 4
Partition Points: 
- 1 to 25000
- 25001 to 50000  
- 50001 to 75000
- 75001 to 100000

Pipeline Partitioning:
- Pass Through: Simple transformations
- Hash Auto-Keys: Complex transformations requiring redistribution
- Key Range: When maintaining sorted order
\`\`\`

**Database Optimization:**
\`\`\`sql
-- Source optimization
-- Use SQL override in Source Qualifier
SELECT /*+ INDEX(c IDX_CUSTOMER_MODIFIED) */
    c.customer_id,
    c.customer_name,
    c.email,
    c.phone,
    c.address,
    c.city,
    c.state,
    c.zip_code,
    c.modified_date
FROM customers c
WHERE c.modified_date >= TO_DATE('$$LAST_EXTRACT_DATE', 'YYYY-MM-DD')
AND c.status = 'ACTIVE'
ORDER BY c.customer_id;

-- Target optimization
-- Bulk load configuration
Target Properties:
- Bulk Mode: Yes
- SQL*Loader Options: DIRECT=TRUE, PARALLEL=TRUE
- Pre-SQL: TRUNCATE TABLE staging_customers
- Post-SQL: 
    ANALYZE TABLE dim_customer COMPUTE STATISTICS;
    UPDATE control_table SET last_processed = SYSDATE 
    WHERE table_name = 'CUSTOMERS';
\`\`\`

## Troubleshooting Guide

### Common Performance Issues

**Lookup Performance:**
\`\`\`
Problem: Slow lookup transformation
Diagnosis:
- Check lookup cache size vs available memory
- Verify lookup query optimization
- Check for proper indexing on lookup columns

Solution:
-- Optimize lookup query
SELECT /*+ INDEX(dim_table IDX_LOOKUP_KEY) */
    lookup_key, 
    return_column1,
    return_column2
FROM dimension_table
WHERE active_flag = 'Y';

-- Tune cache settings
Lookup Cache Size: Set to accommodate full lookup dataset
Persistent Cache: Enable for frequently used lookups
Shared Cache: Enable when multiple sessions use same lookup
\`\`\`

**Session Hangs:**
\`\`\`
Problem: Session appears to hang
Diagnosis Steps:
1. Check session log for last activity
2. Monitor database locks and blocking sessions
3. Check available disk space in temp directories
4. Verify network connectivity
5. Check Integration Service resource usage

Common Causes & Solutions:
- Database locks: Kill blocking sessions, optimize SQL
- Insufficient temp space: Clean up temp directories, allocate more space  
- Memory issues: Reduce DTM buffer sizes, restart Integration Service
- Network timeouts: Increase connection timeout values
\`\`\`

### Error Resolution

**Data Quality Issues:**
\`\`\`sql
-- Implement comprehensive data profiling
SELECT 
    'NULL_CUSTOMER_NAME' as issue_type,
    COUNT(*) as issue_count,
    ROUND(COUNT(*) * 100.0 / (SELECT COUNT(*) FROM customers), 2) as percentage
FROM customers 
WHERE customer_name IS NULL

UNION ALL

SELECT 
    'INVALID_EMAIL' as issue_type,
    COUNT(*) as issue_count,
    ROUND(COUNT(*) * 100.0 / (SELECT COUNT(*) FROM customers), 2) as percentage
FROM customers 
WHERE email NOT LIKE '%@%.%'

UNION ALL

SELECT 
    'DUPLICATE_RECORDS' as issue_type,
    COUNT(*) - COUNT(DISTINCT customer_id) as issue_count,
    ROUND((COUNT(*) - COUNT(DISTINCT customer_id)) * 100.0 / COUNT(*), 2) as percentage
FROM customers;
\`\`\`

**Recovery Procedures:**
\`\`\`bash
#!/bin/bash
# Recovery script for failed ETL runs

WORKFLOW_NAME=$1
RECOVERY_POINT=$2

echo "Starting recovery for workflow: $WORKFLOW_NAME"
echo "Recovery point: $RECOVERY_POINT"

# Stop any running instances
pmcmd stopworkflow -d $DOMAIN -s $SERVICE -f PRODUCTION -w $WORKFLOW_NAME

# Clean up temporary files
rm -rf /temp/informatica/$WORKFLOW_NAME/*

# Reset session state
pmcmd clearsessionlog -d $DOMAIN -s $SERVICE -f PRODUCTION -w $WORKFLOW_NAME

# Restore from checkpoint if available
if [ "$RECOVERY_POINT" != "" ]; then
    pmcmd recoversession -d $DOMAIN -s $SERVICE -f PRODUCTION \
        -w $WORKFLOW_NAME -s $SESSION_NAME -r $RECOVERY_POINT
fi

# Restart workflow
pmcmd startworkflow -d $DOMAIN -s $SERVICE -f PRODUCTION -w $WORKFLOW_NAME

echo "Recovery process completed"
\`\`\`

## Enterprise Best Practices

### Change Management

**Development Lifecycle:**
\`\`\`
1. Requirements Analysis
   - Business requirements documentation
   - Technical design specifications
   - Data mapping documents
   - Performance requirements

2. Development Phase
   - Code development in DEV environment
   - Unit testing
   - Code review checklist
   - Documentation updates

3. Testing Phase
   - Integration testing in TEST environment
   - Performance testing
   - User acceptance testing
   - Security testing

4. Deployment Phase
   - Production deployment
   - Smoke testing
   - Performance monitoring
   - User communication

5. Maintenance Phase
   - Ongoing monitoring
   - Performance optimization
   - Issue resolution
   - Enhancement requests
\`\`\`

### Documentation Standards

**Technical Documentation:**
\`\`\`
Mapping Specification Template:
================================

1. Overview
   - Business Purpose
   - Data Sources
   - Target Systems
   - Processing Frequency

2. Data Flow
   - Source to Target Mapping
   - Transformation Logic
   - Business Rules
   - Data Quality Rules

3. Technical Details
   - Performance Requirements
   - Error Handling
   - Recovery Procedures
   - Dependencies

4. Testing
   - Test Scenarios
   - Expected Results
   - Performance Benchmarks
   - Validation Procedures

5. Deployment
   - Environment Configuration
   - Parameter Settings
   - Monitoring Setup
   - Rollback Procedures
\`\`\`

### Disaster Recovery

**Business Continuity Planning:**
\`\`\`
Recovery Time Objective (RTO): 4 hours
Recovery Point Objective (RPO): 1 hour

DR Procedures:
1. Activate DR site infrastructure
2. Restore repository from latest backup
3. Update connection objects for DR environment
4. Restart critical workflows
5. Validate data processing
6. Notify stakeholders of DR activation

Failback Procedures:
1. Synchronize data between DR and primary
2. Prepare primary site for resumption
3. Schedule failback window
4. Execute failback procedures
5. Validate primary site operations
6. Deactivate DR site
\`\`\`

## Conclusion

You now have comprehensive knowledge of Informatica PowerCenter enterprise deployment and management. This includes environment setup, monitoring, performance tuning, and troubleshooting skills essential for production ETL operations.

**Next Steps for Career Advancement:**
- Informatica Cloud (IICS) training
- Big Data integration with Informatica BDM
- Real-time processing with Informatica Streaming
- Data quality management with Informatica DQ
- Master Data Management with Informatica MDM`
    }
  ]
};